import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Copy,
  Eye,
  MousePointer,
  DollarSign,
  Calendar,
  Target,
  Image
} from 'lucide-react'
import { Button, Badge, Card, CardHeader, CardBody, ConfirmModal } from '../../components/ui'
import type { Campaign, Creative } from '../../types'

// Mock data
const mockCampaign: Campaign = {
  id: '1',
  advertiserId: 'adv-1',
  name: '春のゲーム祭り',
  description: '春の新作ゲームプロモーション。期間限定で新作タイトルを大々的に告知します。',
  budget: 1000000,
  dailyBudget: 50000,
  spentAmount: 800000,
  bidType: 'cpm',
  bidAmount: 500,
  startDate: '2025-12-01',
  endDate: '2025-12-31',
  status: 'active',
  priority: 1,
  frequencyCap: 3,
  frequencyCapPeriod: 'day',
  createdAt: '2025-11-15',
  updatedAt: '2025-12-01',
  advertiser: { id: 'adv-1', name: 'ゲーム会社A', companyName: 'ゲーム株式会社A', status: 'active', createdAt: '', updatedAt: '' },
  targeting: [
    { id: '1', campaignId: '1', targetType: 'store_group', targetValue: '関東地区', includeExclude: 'include', createdAt: '' },
    { id: '2', campaignId: '1', targetType: 'prefecture', targetValue: '東京都', includeExclude: 'include', createdAt: '' },
    { id: '3', campaignId: '1', targetType: 'time', targetValue: '18:00-23:00', includeExclude: 'include', createdAt: '' },
    { id: '4', campaignId: '1', targetType: 'age_range', targetValue: '20-29', includeExclude: 'include', createdAt: '' },
  ],
}

const mockCreatives: Creative[] = [
  {
    id: 'cr-1',
    campaignId: '1',
    name: 'メインバナー_春ver1',
    creativeType: 'image',
    fileUrl: 'https://placehold.co/728x280/3b82f6/ffffff?text=Main+Banner',
    clickUrl: 'https://example.com/game',
    width: 728,
    height: 280,
    altText: '春のゲーム祭り',
    status: 'approved',
    createdAt: '2025-11-20',
    updatedAt: '2025-11-22',
  },
  {
    id: 'cr-2',
    campaignId: '1',
    name: '正方形バナー_春ver1',
    creativeType: 'image',
    fileUrl: 'https://placehold.co/300x250/10b981/ffffff?text=Square+Banner',
    clickUrl: 'https://example.com/game',
    width: 300,
    height: 250,
    altText: '春のゲーム祭り',
    status: 'pending',
    createdAt: '2025-11-20',
    updatedAt: '2025-11-20',
  },
]

const statusConfig = {
  draft: { label: '下書き', variant: 'gray' as const },
  pending: { label: '審査中', variant: 'warning' as const },
  approved: { label: '承認済', variant: 'primary' as const },
  active: { label: '配信中', variant: 'success' as const },
  paused: { label: '停止中', variant: 'danger' as const },
  completed: { label: '終了', variant: 'gray' as const },
}

const creativeStatusConfig = {
  pending: { label: '審査中', variant: 'warning' as const },
  approved: { label: '承認済', variant: 'success' as const },
  rejected: { label: '却下', variant: 'danger' as const },
}

const targetTypeLabels: Record<string, string> = {
  store: '店舗',
  store_group: '店舗グループ',
  prefecture: '都道府県',
  city: '市区町村',
  time: '時間帯',
  day_of_week: '曜日',
  age_range: '年齢層',
  gender: '性別',
  member_rank: '会員ランク',
}

export default function CampaignDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'targeting' | 'creatives' | 'report'>('overview')

  // In real app, fetch campaign by id
  const campaign = mockCampaign
  const creatives = mockCreatives
  const status = statusConfig[campaign.status]
  const progress = campaign.budget ? Math.round((campaign.spentAmount / campaign.budget) * 100) : 0

  const handleDelete = () => {
    console.log('Delete campaign:', id)
    setShowDeleteModal(false)
    navigate('/campaigns')
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/campaigns" className="breadcrumb-item flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          キャンペーン一覧
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="text-gray-900">{campaign.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
            <Badge variant={status.variant} dot>{status.label}</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">{campaign.advertiser?.name}</p>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'active' ? (
            <Button variant="secondary" leftIcon={<Pause className="w-4 h-4" />}>
              停止
            </Button>
          ) : campaign.status === 'paused' ? (
            <Button variant="success" leftIcon={<Play className="w-4 h-4" />}>
              再開
            </Button>
          ) : null}
          <Button variant="secondary" leftIcon={<Copy className="w-4 h-4" />}>
            複製
          </Button>
          <Link to={`/campaigns/${id}/edit`}>
            <Button variant="secondary" leftIcon={<Edit className="w-4 h-4" />}>
              編集
            </Button>
          </Link>
          <Button variant="danger" leftIcon={<Trash2 className="w-4 h-4" />} onClick={() => setShowDeleteModal(true)}>
            削除
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: '概要' },
            { id: 'targeting', label: 'ターゲティング' },
            { id: 'creatives', label: 'クリエイティブ' },
            { id: 'report', label: 'レポート' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900">基本情報</h3>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">キャンペーン名</dt>
                    <dd className="mt-1 text-gray-900">{campaign.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">広告主</dt>
                    <dd className="mt-1 text-gray-900">{campaign.advertiser?.name}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm text-gray-500">説明</dt>
                    <dd className="mt-1 text-gray-900">{campaign.description || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">配信期間</dt>
                    <dd className="mt-1 text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      {new Date(campaign.startDate).toLocaleDateString('ja-JP')} 〜 {new Date(campaign.endDate).toLocaleDateString('ja-JP')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">入札方式</dt>
                    <dd className="mt-1 text-gray-900">
                      {campaign.bidType.toUpperCase()} - ¥{campaign.bidAmount?.toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">フリークエンシーキャップ</dt>
                    <dd className="mt-1 text-gray-900">
                      {campaign.frequencyCap ? `${campaign.frequencyCap}回/${campaign.frequencyCapPeriod === 'day' ? '日' : campaign.frequencyCapPeriod === 'hour' ? '時間' : '週'}` : '設定なし'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">優先度</dt>
                    <dd className="mt-1 text-gray-900">
                      {campaign.priority === 0 ? '通常' : campaign.priority === 1 ? '高' : '最優先'}
                    </dd>
                  </div>
                </dl>
              </CardBody>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardBody className="text-center">
                  <Eye className="w-8 h-8 text-blue-500 mx-auto" />
                  <p className="text-2xl font-bold text-gray-900 mt-2">234,567</p>
                  <p className="text-sm text-gray-500">インプレッション</p>
                </CardBody>
              </Card>
              <Card>
                <CardBody className="text-center">
                  <MousePointer className="w-8 h-8 text-green-500 mx-auto" />
                  <p className="text-2xl font-bold text-gray-900 mt-2">8,765</p>
                  <p className="text-sm text-gray-500">クリック</p>
                </CardBody>
              </Card>
              <Card>
                <CardBody className="text-center">
                  <DollarSign className="w-8 h-8 text-purple-500 mx-auto" />
                  <p className="text-2xl font-bold text-gray-900 mt-2">3.73%</p>
                  <p className="text-sm text-gray-500">CTR</p>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Budget */}
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900">予算</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">消化額</span>
                      <span className="font-medium text-gray-900">¥{campaign.spentAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">総予算</span>
                      <span className="text-gray-600">¥{campaign.budget?.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full transition-all ${
                          progress >= 90 ? 'bg-red-500' : 
                          progress >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">{progress}% 消化</p>
                  </div>
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">日予算</span>
                      <span className="text-gray-900">¥{campaign.dailyBudget?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Targeting Summary */}
            <Card>
              <CardHeader className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">ターゲティング</h3>
                <Target className="w-4 h-4 text-gray-400" />
              </CardHeader>
              <CardBody>
                <div className="space-y-2">
                  {campaign.targeting?.slice(0, 4).map((target) => (
                    <div key={target.id} className="flex items-center text-sm">
                      <span className="text-gray-500 w-24">{targetTypeLabels[target.targetType]}</span>
                      <span className="text-gray-900">{target.targetValue}</span>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-gray-900">ターゲティング設定</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-6">
              {campaign.targeting?.map((target) => (
                <div key={target.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{targetTypeLabels[target.targetType]}</span>
                    <p className="text-gray-600 mt-1">{target.targetValue}</p>
                  </div>
                  <Badge variant={target.includeExclude === 'include' ? 'success' : 'danger'}>
                    {target.includeExclude === 'include' ? '含める' : '除外'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {activeTab === 'creatives' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {creatives.map((creative) => {
            const creativeStatus = creativeStatusConfig[creative.status]
            return (
              <Card key={creative.id}>
                <div className="aspect-video bg-gray-100 relative overflow-hidden rounded-t-lg">
                  <img 
                    src={creative.fileUrl} 
                    alt={creative.altText || creative.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardBody>
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{creative.name}</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {creative.width} x {creative.height} px | {creative.creativeType}
                      </p>
                    </div>
                    <Badge variant={creativeStatus.variant}>{creativeStatus.label}</Badge>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="secondary" leftIcon={<Eye className="w-3 h-3" />}>
                      プレビュー
                    </Button>
                    <Button size="sm" variant="secondary" leftIcon={<Edit className="w-3 h-3" />}>
                      編集
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )
          })}
          
          {/* Add Creative Card */}
          <Card className="border-dashed border-2 border-gray-300 hover:border-primary-400 transition-colors">
            <CardBody className="flex flex-col items-center justify-center h-full min-h-[200px] cursor-pointer">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <Image className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">クリエイティブを追加</p>
              <p className="text-sm text-gray-400 mt-1">画像/HTML/動画をアップロード</p>
            </CardBody>
          </Card>
        </div>
      )}

      {activeTab === 'report' && (
        <Card>
          <CardBody className="text-center py-12">
            <p className="text-gray-500">レポート機能は準備中です</p>
          </CardBody>
        </Card>
      )}

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="キャンペーンを削除"
        message="このキャンペーンを削除しますか？この操作は取り消せません。"
        confirmText="削除する"
        variant="danger"
      />
    </div>
  )
}

